import { Calendar } from 'lucide-react';
import { clsx } from 'clsx';

export default function DateFilter({ dateFrom, dateTo, onDateFromChange, onDateToChange, onApply, onReset }) {
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handlePreset = (type) => {
    const now = new Date();
    const todayStr = formatDate(now);

    if (type === 'today') {
      onDateFromChange(todayStr);
      onDateToChange(todayStr);
      onApply?.(todayStr, todayStr);
    } else if (type === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekStart = formatDate(weekAgo);
      onDateFromChange(weekStart);
      onDateToChange(todayStr);
      onApply?.(weekStart, todayStr);
    } else if (type === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      const monthStart = formatDate(monthAgo);
      onDateFromChange(monthStart);
      onDateToChange(todayStr);
      onApply?.(monthStart, todayStr);
    } else if (type === 'year') {
      const yearAgo = new Date(now);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      const yearStart = formatDate(yearAgo);
      onDateFromChange(yearStart);
      onDateToChange(todayStr);
      onApply?.(yearStart, todayStr);
    }
  };

  const now = new Date();
  const todayStr = formatDate(now);

  const weekAgo = new Date(now);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const weekStartStr = formatDate(weekAgo);

  const monthAgo = new Date(now);
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  const monthStartStr = formatDate(monthAgo);

  const yearAgo = new Date(now);
  yearAgo.setFullYear(yearAgo.getFullYear() - 1);
  const yearStartStr = formatDate(yearAgo);

  const isToday = dateFrom === todayStr && dateTo === todayStr;
  const isWeek = !isToday && dateFrom === weekStartStr && dateTo === todayStr;
  const isMonth = !isToday && dateFrom === monthStartStr && dateTo === todayStr;
  const isYear = !isToday && dateFrom === yearStartStr && dateTo === todayStr;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Period & Date Filter</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => handlePreset('today')}
            className={clsx(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-all',
              isToday
                ? 'bg-emerald-500 font-bold text-white shadow-xs'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            Daily
          </button>
          <button
            type="button"
            onClick={() => handlePreset('week')}
            className={clsx(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-all',
              isWeek
                ? 'bg-emerald-500 font-bold text-white shadow-xs'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            Weekly
          </button>
          <button
            type="button"
            onClick={() => handlePreset('month')}
            className={clsx(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-all',
              isMonth
                ? 'bg-emerald-500 font-bold text-white shadow-xs'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => handlePreset('year')}
            className={clsx(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-all',
              isYear
                ? 'bg-emerald-500 font-bold text-white shadow-xs'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            Yearly
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={() => onApply?.()} className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 shadow-xs">
            Apply
          </button>
          {(dateFrom || dateTo) && (
            <button onClick={onReset} className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
